import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { AnalyticsData } from "@/types/analytics";
import { ProductivityScore } from "@/components/analytics/ProductivityScore";
import { EisenhowerMatrix } from "@/components/analytics/EisenhowerMatrix";
import { AnalyticsGraphs } from "@/components/analytics/AnalyticsGraphs";
import { ExportAnalytics } from "@/components/analytics/ExportAnalytics";

export function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAnalytics() {
      try {
        const boards = await api.get<any[]>('/boards');

        if (!boards || boards.length === 0) {
          setData(emptyAnalytics());
          return;
        }

        const columnArrays = await Promise.all(
          boards.map((b: any) => api.get<any[]>(`/boards/${b.id}/columns`))
        );
        const columns = columnArrays.flat();
        const cards = columns.flatMap((c: any) => c.cards ?? []);

        setData(buildAnalytics(boards, columns, cards));
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError(err instanceof Error ? err.message : "Failed to fetch analytics data");
      } finally {
        setLoading(false);
      }
    }
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
        {data && (
          <ExportAnalytics
            data={{
              productivityScore: data.productivity.daily.score,
              totalTasks: data.totalTasks,
              completedTasks: data.completedTasks,
              eisenhowerMatrix: {
                urgent_important: data.eisenhowerMatrix.urgent.important,
                not_urgent_important: data.eisenhowerMatrix.notUrgent.important,
                urgent_not_important: data.eisenhowerMatrix.urgent.notImportant,
                not_urgent_not_important: data.eisenhowerMatrix.notUrgent.notImportant,
              },
              tasksByPriority: data.tasksByPriority,
            }}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Task Overview</h2>
          <div className="space-y-2">
            <p>Total Tasks: {data?.totalTasks || 0}</p>
            <p>Completed Tasks: {data?.completedTasks || 0}</p>
            <p>Completion Rate: {(data?.completionRate || 0).toFixed(1)}%</p>
            <p>Avg. Completion Time: {(data?.averageCompletionTime || 0).toFixed(1)}h</p>
          </div>
        </div>
        <div className="bg-slate-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Project Health</h2>
          <div className="space-y-2">
            <p>Total Projects: {data?.projectHealth.totalProjects || 0}</p>
            <p>Active Projects: {data?.projectHealth.activeProjects || 0}</p>
            <p>Completed Projects: {data?.projectHealth.completedProjects || 0}</p>
          </div>
        </div>
        <div className="bg-slate-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Task Priorities</h2>
          <div className="space-y-2">
            <p>Critical: {data?.tasksByPriority.critical || 0}</p>
            <p>Important: {data?.tasksByPriority.important || 0}</p>
            <p>Normal: {data?.tasksByPriority.normal || 0}</p>
          </div>
        </div>
      </div>

      {data && <AnalyticsGraphs tasksByDay={data.tasksByDay} tasksByPriority={data.tasksByPriority} completionRate={data.completionRate} />}
      {data && <div className="mb-8"><ProductivityScore data={data.productivity} /></div>}
      {data && <div className="mb-8"><EisenhowerMatrix data={data.eisenhowerMatrix} /></div>}
    </div>
  );
}

function emptyAnalytics(): AnalyticsData {
  return {
    totalTasks: 0, completedTasks: 0, completionRate: 0, averageCompletionTime: 0,
    tasksByPriority: { critical: 0, important: 0, normal: 0 },
    tasksByDay: {},
    projectHealth: { totalProjects: 0, activeProjects: 0, completedProjects: 0 },
    taskAging: [],
    productivity: {
      daily: { score: 0, tasksCompleted: 0, avgCompletionTime: "0h", urgentResponseTime: "0h" },
      weekly: { score: 0, trend: "stable", improvement: 0 },
      monthly: { score: 0, trend: "stable", improvement: 0 },
    },
    optimization: [],
    eisenhowerMatrix: { urgent: { important: 0, notImportant: 0 }, notUrgent: { important: 0, notImportant: 0 } },
    completionTrends: { monthly: {}, quarterly: {}, yearly: {}, seasonalPatterns: { spring: 0, summer: 0, fall: 0, winter: 0 } },
  };
}

function buildAnalytics(boards: any[], columns: any[], cards: any[]): AnalyticsData {
  const d = emptyAnalytics();
  d.projectHealth.totalProjects = boards.length;

  let totalCompletionTime = 0;
  let completedCount = 0;

  cards.forEach(card => {
    d.totalTasks++;
    const col = columns.find(c => c.id === card.column_id);
    const isDone = col?.title?.toLowerCase().includes('done');
    if (isDone) {
      d.completedTasks++;
      if (card.completed_at) {
        totalCompletionTime += new Date(card.completed_at).getTime() - new Date(card.created_at).getTime();
        completedCount++;
      }
    }
    const pri = card.priority?.toLowerCase();
    if (pri === 'critical') d.tasksByPriority.critical++;
    else if (pri === 'important') d.tasksByPriority.important++;
    else d.tasksByPriority.normal++;

    const day = new Date(card.created_at).toISOString().split('T')[0];
    d.tasksByDay[day] = (d.tasksByDay[day] || 0) + 1;

    const isUrgent = pri === 'critical';
    const isImportant = pri === 'important' || pri === 'critical';
    if (isUrgent) isImportant ? d.eisenhowerMatrix.urgent.important++ : d.eisenhowerMatrix.urgent.notImportant++;
    else isImportant ? d.eisenhowerMatrix.notUrgent.important++ : d.eisenhowerMatrix.notUrgent.notImportant++;
  });

  d.completionRate = d.totalTasks > 0 ? (d.completedTasks / d.totalTasks) * 100 : 0;
  d.averageCompletionTime = completedCount > 0 ? totalCompletionTime / completedCount / 3600000 : 0;

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  d.productivity.daily = {
    score: Math.min(100, (d.tasksByDay[todayStr] || 0) * 10),
    tasksCompleted: d.tasksByDay[todayStr] || 0,
    avgCompletionTime: `${d.averageCompletionTime.toFixed(1)}h`,
    urgentResponseTime: "2h",
  };

  const lastWeek = new Date(today); lastWeek.setDate(lastWeek.getDate() - 7);
  const lastMonth = new Date(today); lastMonth.setMonth(lastMonth.getMonth() - 1);
  const weekly = Object.entries(d.tasksByDay).filter(([dt]) => new Date(dt) >= lastWeek).reduce((s, [, n]) => s + n, 0);
  const monthly = Object.entries(d.tasksByDay).filter(([dt]) => new Date(dt) >= lastMonth).reduce((s, [, n]) => s + n, 0);

  d.productivity.weekly = { score: Math.min(100, weekly * 2), trend: weekly > 20 ? 'up' : weekly > 10 ? 'stable' : 'down', improvement: weekly > 20 ? 15 : weekly > 10 ? 0 : -10 };
  d.productivity.monthly = { score: Math.min(100, monthly), trend: monthly > 80 ? 'up' : monthly > 40 ? 'stable' : 'down', improvement: monthly > 80 ? 20 : monthly > 40 ? 0 : -15 };

  d.projectHealth.activeProjects = boards.filter(b => columns.some(c => c.board_id === b.id && cards.some(k => k.column_id === c.id))).length;
  d.projectHealth.completedProjects = boards.filter(b => columns.filter(c => c.board_id === b.id).every(c => c.title?.toLowerCase().includes('done') || !cards.some(k => k.column_id === c.id))).length;

  return d;
}
